import { CHEST_SHUT_VERB, SIXTEENTHS } from "odyssey-shared";
import { dialogueSounds, npcPlaceOf } from "./dialogue/sounds.ts";
import type { SessionParts } from "./session-types.ts";
import { say } from "../words.ts";

/**
 * Hands the page's stores the way to send on the current connection: chat, the emote wheel, the PvP flag switch, the
 * bag's use, move and edits, the chest's take and close, the bar's arrangement, the dialogue window's choice and
 * close (and the sound of its conversations), and the respawn. Each send that is an
 * act of the player goes only once the world is shown and, where the Game Master's freeze refuses it, only while the
 * player is not frozen; each returns false when the connection did not send it. The chest's notices go to the message
 * line and each item it gains to the loot toasts. The Map's notice that it is open in another window goes there too.
 *
 * @param parts The session's parts.
 */
export function bindSends(parts: SessionParts): void {
  const { options, host, state, connection, gate } = parts;
  options.chat?.bind((request) => connection()?.sendChat(request) ?? false);
  options.emotes?.bindSend((kind, id, target) => state.revealed && (connection()?.sendEmote(kind, id, target) ?? false));
  options.hud.bindFlagToggle((wanted) => !gate.refuses() && (connection()?.sendFlagToggle(wanted) ?? false));
  options.bag?.bindUse((slot, item) => state.revealed && !gate.refuses() && (connection()?.sendUseItem(slot, item) ?? false));
  options.bag?.bindMove((from, to) => state.revealed && !gate.refuses() && (connection()?.sendBagMove(from, to) ?? false));
  options.bag?.bindEdits(
    (slot) => state.revealed && !gate.refuses() && (connection()?.sendBagSplit(slot) ?? false),
    (slot, item, quantity) => state.revealed && !gate.refuses() && (connection()?.sendDestroyItem(slot, item, quantity) ?? false),
    (by) => state.revealed && !gate.refuses() && (connection()?.sendBagSort(by) ?? false),
    (slot, kept) => state.revealed && !gate.refuses() && (connection()?.sendBagKeep(slot, kept) ?? false),
    (slot, item, quantity) => state.revealed && !gate.refuses() && (connection()?.sendDropItem(slot, item, quantity) ?? false),
  );
  options.chest?.bindTake((handle, lines) => state.revealed && !gate.refuses() && (connection()?.sendLootTake(handle, lines) ?? false));
  options.chest?.bindClose((handle) => {
    if (state.revealed) {
      connection()?.sendInteract(handle, CHEST_SHUT_VERB);
    }
  });
  options.chest?.onNotice((notice) => host.messageLine.say(say(`chest.lines.${notice}`)));
  options.chest?.onGained((gain) => options.loot?.show(gain.item, gain.quantity));
  options.worldMap?.onBringForward?.(() => host.messageLine.say(say("worldMap.openElsewhere")));
  options.belt?.bindSend((seq, slots, selected) => state.revealed && (connection()?.sendToolBeltSet(seq, slots, selected) ?? false));
  options.companion?.bindSend((out) => state.revealed && (connection()?.sendCompanionSet(out) ?? false));
  options.arranged?.bindSend((seq, slots) => state.revealed && (connection()?.sendBarSet(seq, slots) ?? false));
  const cues = options.sounds;
  options.dialogue?.listen(
    cues === undefined
      ? null
      : dialogueSounds({
          cue: (sound, event, dx, dy, handle) => cues.npcCue(sound, event, dx, dy, handle),
          where: (handle) => npcPlaceOf(connection()?.view.entities.get(handle), connection()?.view.entities.get(state.handle), (appearance) => host.world()?.npcLooks[appearance]?.sound ?? null, SIXTEENTHS),
        }),
  );
  options.dialogue?.bind(
    (handle, node, choice) => state.revealed && !gate.refuses() && (connection()?.sendDialogueChoose(handle, node, choice) ?? false),
    (handle) => {
      connection()?.sendDialogueClose(handle);
    },
    (name) => state.revealed && !gate.refuses() && (connection()?.sendCompanionName(name) ?? false),
  );
  options.death?.bindRespawn((point) => state.revealed && (connection()?.sendRespawn(point) ?? false));
}
